import { useLocalStorage } from '@mantine/hooks';
import isEqual from 'lodash/isEqual';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
  JobCostCodeSummaryGpDto,
  WbsAccountRequestDto,
  WbsAdjustmentDto,
  WbsAdjustmentRequestDto,
  WbsAdjustmentTypeDto,
  WbsRequestDto,
  WbsTreeResponse,
} from 'src/api/FiretrolApiClient';
import { useCtcJob } from 'src/api/queries/ctc/useCtcJob';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import {
  fetchWbsAdjustments,
  useWbsAdjustments,
} from 'src/api/queries/wbs/useWbsAdjustments';
import { useWbsAdjustmentTypes } from 'src/api/queries/wbs/useWbsAdjustmentTypes';
import { useSaveJobWbs } from 'src/api/queries/wbs/useWbsMutations';
import { useWbsAccountMutations } from 'src/api/queries/wbs/useWbsAccountMutations';
import { useWbsAdjustmentMutations } from 'src/api/queries/wbs/useWbsAdjustmentMutations';
import { queryKeys } from 'src/api/queries/queryKeys';
import {
  getErrorMessage,
  logger,
  convertAccountsToTreeData,
  findNodeInTree,
  removeNodeFromTree,
  traverseWbsTree,
  updateNodeInTree,
} from 'src/utils';
import { useJobs } from 'src/hooks/useJobs';
import { DisplayOptions } from 'src/routes/Jobs/WBS/components/DisplayOptionsModal';
import { WbsTreeData } from 'src/routes/Jobs/WBS/components/types';

interface WbsContextState {
  // Core WBS Data
  wbsData: WbsTreeResponse | null;
  treeData: WbsTreeData[];
  accountMap: Map<string, WbsTreeData>;
  selectedTreeRow: WbsTreeData | null;
  summary: JobCostCodeSummaryGpDto[];

  // Loading States
  loading: boolean;
  saving: boolean;
  adjustmentsLoading: boolean;
  adjustmentTypesLoading: boolean;

  // Error States
  error: string | null;
  adjustmentsError: string | null;
  adjustmentTypesError: string | null;

  // Adjustment Data
  adjustments: WbsAdjustmentDto[];
  adjustmentTypes: WbsAdjustmentTypeDto[];
  selectedAdjustment: WbsAdjustmentDto | null;
  adjustmentsRefreshKey: number;

  // Job Root Node
  jobRootNode: WbsTreeData | null;
  isJobRootSelected: boolean;

  // Tree State Helpers
  isRollUpAccountSelected: boolean;
  isChargeCodeSelected: boolean;
  isPlaceholderAccountSelected: boolean;
  isCTCConnectedAccountSelected: boolean;
  canCreateRollUpAccount: boolean;
  canAddChargeCode: boolean;
  canDuplicate: boolean;
  canDelete: boolean;

  // Display Options
  displayOptions: DisplayOptions;

  // Cross-feature staleness (set by Progress Viewer edits)
  wbsDirty: boolean;
}

interface WbsContextActions {
  // Core WBS Operations
  loadWBS: () => Promise<void>;
  saveWBS: (wbsRequest: WbsRequestDto) => Promise<void>;
  refreshWbs: () => Promise<void>;
  markWbsStale: () => void;

  // Tree Operations
  selectTreeRow: (row: WbsTreeData | null) => void;
  onTreeSelect: (keys: React.Key[], data?: unknown) => void;
  findRootAncestor: (node: WbsTreeData) => WbsTreeData | null;
  collectLeafDescendants: (node: WbsTreeData) => WbsTreeData[];

  // Account Operations
  createAccount: (accountRequest: WbsAccountRequestDto) => Promise<WbsTreeData>;
  updateAccount: (
    accountId: string,
    accountRequest: WbsAccountRequestDto,
  ) => Promise<void>;
  deleteAccount: (accountId: string) => Promise<void>;

  // Account CRUD Actions
  createRollUpAccount: () => Promise<void>;
  createRootNode: () => Promise<void>;
  addCostCode: () => Promise<void>;
  duplicateCode: () => Promise<void>;
  removeCode: () => Promise<void>;

  // Account Save Callback
  onAccountSaved: (
    row: WbsTreeData,
    updates: Partial<WbsTreeData>,
  ) => Promise<void>;

  // Adjustment Operations
  loadAdjustments: () => Promise<void>;
  createAdjustment: (
    adjustmentRequest: WbsAdjustmentRequestDto,
  ) => Promise<void>;
  updateAdjustment: (
    adjustmentId: string,
    adjustmentRequest: WbsAdjustmentRequestDto,
  ) => Promise<void>;
  deleteAdjustment: (adjustmentId: string) => Promise<void>;
  selectAdjustment: (adjustment: WbsAdjustmentDto | null) => void;
  onAdjustmentSaved: () => Promise<void>;

  // Adjustment Types
  loadAdjustmentTypes: () => Promise<void>;

  // Display Options Operations
  updateDisplayOptions: (options: Partial<DisplayOptions>) => void;

  // Utility Functions
  createAccountRequest: (
    data: Partial<WbsTreeData>,
    updates?: Partial<WbsTreeData>,
  ) => WbsAccountRequestDto;
  getTreeNodeIcon: (node?: WbsTreeData) => React.ReactNode;
  isRollUpAccount: (row: WbsTreeData) => boolean;
  isChargeCodeAccount: (row: WbsTreeData) => boolean;
  isPlaceholderAccount: (apply: boolean) => boolean;
  isCTCConnectedAccount: (row: WbsTreeData) => boolean;
}

type WbsContextType = WbsContextState & WbsContextActions;

const WbsContext = createContext<WbsContextType | undefined>(undefined);

const EMPTY_SUMMARY: JobCostCodeSummaryGpDto[] = [];
const EMPTY_ADJUSTMENTS: WbsAdjustmentDto[] = [];
const EMPTY_ADJUSTMENT_TYPES: WbsAdjustmentTypeDto[] = [];

export const useWbs = (): WbsContextType => {
  const context = useContext(WbsContext);
  if (!context) {
    throw new Error('useWbs must be used within a WbsProvider');
  }
  return context;
};

interface WbsProviderProps {
  children: React.ReactNode;
}

export const WbsProvider: React.FC<WbsProviderProps> = ({ children }) => {
  const qc = useQueryClient();
  const { selectedJob } = useJobs();
  const jobId = selectedJob?.id;

  // Core WBS Data
  const wbsQuery = useJobWbs(jobId);
  const ctcQuery = useCtcJob(
    selectedJob && selectedJob.jobType !== 'service'
      ? selectedJob.jobNumber
      : undefined,
  );
  const summary = ctcQuery.data?.summary ?? EMPTY_SUMMARY;

  // wbsData is null for a job with no accounts yet: several call sites use it
  // as the "WBS exists" guard, not just as the response envelope.
  const wbsResponse = wbsQuery.data ?? null;
  const wbsData = wbsResponse?.wbsAccount?.length ? wbsResponse : null;

  const serverTree = useMemo(
    () => convertAccountsToTreeData(wbsResponse?.wbsAccount ?? []),
    [wbsResponse],
  );

  // Tree state is seeded from the server and then edited in place, so the
  // provider owns it rather than deriving it on every render.
  const [treeData, setTreeData] = React.useState<WbsTreeData[]>([]);
  const [accountMap, setAccountMap] = React.useState<Map<string, WbsTreeData>>(
    new Map(),
  );
  const [selectedTreeRow, setSelectedTreeRow] =
    React.useState<WbsTreeData | null>(null);

  // Set when a Progress Viewer edit lands; cleared when WBS data reloads.
  const [wbsDirty, setWbsDirty] = React.useState(false);
  const markWbsStale = useCallback(() => setWbsDirty(true), []);

  const loading = wbsQuery.isLoading || ctcQuery.isLoading;

  // Error States
  const [error, setError] = React.useState<string | null>(null);

  // Adjustment State
  const [selectedAdjustment, setSelectedAdjustment] =
    React.useState<WbsAdjustmentDto | null>(null);

  const adjustmentAccountId =
    selectedTreeRow && !selectedTreeRow.isArtificial && wbsData
      ? selectedTreeRow.id
      : undefined;
  const adjustmentsQuery = useWbsAdjustments(adjustmentAccountId);
  const adjustments = adjustmentsQuery.data ?? EMPTY_ADJUSTMENTS;
  const adjustmentTypesQuery = useWbsAdjustmentTypes();
  const adjustmentTypes = adjustmentTypesQuery.data ?? EMPTY_ADJUSTMENT_TYPES;

  const saveJobWbs = useSaveJobWbs(
    jobId ?? -1,
    'Job date settings updated successfully!',
  );
  const accountMutations = useWbsAccountMutations(jobId ?? -1);
  const adjustmentMutations = useWbsAdjustmentMutations(
    jobId ?? -1,
    adjustmentAccountId ?? '',
  );
  const saving =
    saveJobWbs.isPending ||
    accountMutations.createAccount.isPending ||
    accountMutations.updateAccount.isPending ||
    accountMutations.deleteAccount.isPending;

  // Display Options State
  const [displayOptions, setDisplayOptions] = useLocalStorage<DisplayOptions>({
    key: 'wbs-display-options',
    defaultValue: {
      hideCostCodes: true,
      alwaysShowCostCodesForCTC: true,
    },
  });

  // Reseed the tree whenever the server view changes, keeping the current
  // selection pointed at the same account where it still exists.
  React.useEffect(() => {
    setTreeData(serverTree);
    setSelectedTreeRow((prev) => {
      const found = prev ? findNodeInTree(serverTree, prev.id) : null;
      return found ?? serverTree[0] ?? null;
    });
    setWbsDirty(false);
  }, [serverTree]);

  // Build account map whenever tree data changes
  React.useEffect(() => {
    const map = new Map<string, WbsTreeData>();
    traverseWbsTree(treeData, (node) => {
      map.set(node.id, node);
    });
    setAccountMap(map);
  }, [treeData]);

  // Helper function to create account request from tree data
  const createAccountRequest = useCallback(
    (
      data: Partial<WbsTreeData>,
      updates: Partial<WbsTreeData> = {},
    ): WbsAccountRequestDto => {
      // Merge data with updates, preferring updates values
      const merged = { ...data, ...updates };
      return {
        parentId: merged.parentId || undefined,
        costCodeId: merged.costCodeId || undefined,
        name: merged.name || '',
        description: merged.description || '',
        accountType: merged.accountType || 'other',
        initialQuantityEstimate: merged.initialQuantityEstimate ?? 0,
        currentQuantityEstimate: merged.currentQuantityEstimate ?? 0,
        currentQuantityActual: merged.currentQuantityActual ?? 0,
        initialHoursEstimate: merged.initialHoursEstimate || 0,
        currentHoursEstimate: merged.currentHoursEstimate || 0,
        currentHoursActual: merged.currentHoursActual || 0,
        unitOfMeasure: merged.unitOfMeasure || '$',
        externalWbsId: merged.externalWbsId || undefined,
        startDate: merged.startDate || undefined,
        finishDate: merged.finishDate || undefined,
        apply: merged.apply,
        active: merged.active,
        timesheetsOnly: merged.timesheetsOnly,
        perDiemEligible: merged.perDiemEligible ?? false,
        scalePayEnabled: merged.scalePayEnabled ?? false,
      };
    },
    [],
  );

  // Helper function to update tree state after account changes
  const updateTreeItem = useCallback(
    (accountId: string, updates: Partial<WbsTreeData>) => {
      setTreeData((prevState) =>
        updateNodeInTree(prevState, accountId, updates),
      );

      // Update selected row if it's the one being edited
      if (selectedTreeRow && selectedTreeRow.id === accountId) {
        setSelectedTreeRow((prev) => (prev ? { ...prev, ...updates } : null));
      }
    },
    [selectedTreeRow],
  );

  // Helper function to remove account from tree state
  const removeTreeItem = useCallback(
    (accountId: string) => {
      setTreeData((prevState) => removeNodeFromTree(prevState, accountId));

      if (selectedTreeRow && selectedTreeRow.id === accountId) {
        setSelectedTreeRow(null);
      }
    },
    [selectedTreeRow],
  );

  // Core WBS Operations
  const refreshWbs = useCallback(async () => {
    if (jobId == null) return;
    setWbsDirty(false);
    await qc.invalidateQueries({ queryKey: queryKeys.wbs.forJob(jobId) });
  }, [jobId, qc]);

  const loadWBS = refreshWbs;

  const saveWBS = useCallback(
    async (wbsRequest: WbsRequestDto) => {
      if (jobId == null) return;
      await saveJobWbs.mutateAsync(wbsRequest);
    },
    [jobId, saveJobWbs],
  );

  // Account Operations
  const createAccount = useCallback(
    async (accountRequest: WbsAccountRequestDto): Promise<WbsTreeData> => {
      if (!wbsData) throw new Error('WBS data not available');

      const newAccount = await accountMutations.createAccount.mutateAsync({
        wbsId: wbsData.id,
        request: accountRequest,
      });
      const newTreeRow: WbsTreeData = {
        id: newAccount.id,
        wbsId: newAccount.wbsId || '',
        parentId: newAccount.parentId || null,
        costCodeId: newAccount.costCodeId || null,
        code: newAccount.code || '',
        name: newAccount.name || '',
        description: newAccount.description || '',
        accountType:
          (newAccount.accountType as 'labor' | 'cost' | 'other') || null,
        initialQuantityEstimate: newAccount.initialQuantityEstimate,
        currentQuantityEstimate: newAccount.currentQuantityEstimate,
        currentQuantityActual: newAccount.currentQuantityActual,
        initialHoursEstimate: newAccount.initialHoursEstimate || 0,
        currentHoursEstimate: newAccount.currentHoursEstimate || 0,
        currentHoursActual: newAccount.currentHoursActual || 0,
        unitOfMeasure:
          (newAccount.unitOfMeasure?.toLowerCase() as
            | '$'
            | 'hours'
            | 'pieces'
            | 'heads'
            | 'feet'
            | ''
            | null) || '$',
        apply: newAccount.apply ?? true,
        active: newAccount.active ?? true,
        archived: newAccount.archived ?? false,
        timesheetsOnly: newAccount.timesheetsOnly ?? false,
        perDiemEligible: newAccount.perDiemEligible ?? false,
        scalePayEnabled: newAccount.scalePayEnabled ?? false,
        externalWbsId: newAccount.externalWbsId || null,
        isCtcConnected: newAccount.isCtcConnected,
        isLeaf: newAccount.isLeaf ?? true,
        createdAt: newAccount.createdAt || new Date(),
        updatedAt: newAccount.updatedAt || new Date(),
        startDate: newAccount.startDate || null,
        finishDate: newAccount.finishDate || null,
        children: [],
      };

      return newTreeRow;
    },
    [wbsData, accountMutations.createAccount],
  );

  const updateAccount = useCallback(
    async (
      accountId: string,
      accountRequest: WbsAccountRequestDto,
    ): Promise<void> => {
      if (!wbsData) throw new Error('WBS data not available');
      await accountMutations.updateAccount.mutateAsync({
        accountId,
        request: accountRequest,
      });
    },
    [wbsData, accountMutations.updateAccount],
  );

  const deleteAccount = useCallback(
    async (accountId: string): Promise<void> => {
      if (!wbsData) throw new Error('WBS data not available');
      await accountMutations.deleteAccount.mutateAsync(accountId);
    },
    [wbsData, accountMutations.deleteAccount],
  );

  // Tree Operations
  const selectTreeRow = useCallback((row: WbsTreeData | null) => {
    setSelectedTreeRow(row);
    setSelectedAdjustment(null); // Clear adjustment selection when changing tree row
  }, []);

  const onTreeSelect = useCallback(
    (keys: React.Key[], _data?: unknown) => {
      if (keys.length === 0) {
        selectTreeRow(null);
        return;
      }

      const key = keys[0];
      // Check if it's the artificial job root first
      if (selectedJob && key === `job-root-${selectedJob.id}`) {
        // Create the job root node on the fly
        const artificalRoot: WbsTreeData = {
          id: `job-root-${selectedJob.id}`,
          wbsId: '',
          parentId: null,
          costCodeId: null,
          code: '',
          name: selectedJob.jobNumber || 'Job',
          description: '',
          accountType: null,
          initialQuantityEstimate: 0,
          currentQuantityEstimate: 0,
          currentQuantityActual: 0,
          initialHoursEstimate: 0,
          currentHoursEstimate: 0,
          currentHoursActual: 0,
          unitOfMeasure: '$',
          apply: true,
          active: true,
          archived: false,
          timesheetsOnly: false,
          perDiemEligible: true,
          scalePayEnabled: false,
          externalWbsId: null,
          isCtcConnected: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          isLeaf: false,
          children: treeData,
          isArtificial: true,
          startDate: null,
          finishDate: null,
        };
        selectTreeRow(artificalRoot);
        return;
      }

      // Otherwise search in the actual tree data
      const target = findNodeInTree(treeData, key as string) || null;
      selectTreeRow(target);
    },
    [treeData, selectTreeRow, selectedJob],
  );

  // Helper to find the root ancestor for a given node
  function findRootAncestor(node: WbsTreeData) {
    let current = node;
    while (current && current.parentId) {
      current = accountMap.get(current.parentId);
    }
    return current;
  }

  // Helper to collect all leaf descendants of a node (excluding the node itself)
  function collectLeafDescendants(node: WbsTreeData): WbsTreeData[] {
    let leaves: WbsTreeData[] = [];
    if (!node.children || node.children.length === 0) {
      // Don't include the node itself (root)
      return [];
    }
    for (const child of node.children) {
      if (child.isLeaf) {
        leaves.push(child);
      } else {
        leaves = leaves.concat(collectLeafDescendants(child));
      }
    }
    return leaves;
  }

  // Account CRUD Actions
  const createRollUpAccount = useCallback(async () => {
    if (!selectedTreeRow || !selectedJob) return;
    try {
      const accountRequest = createAccountRequest(
        { parentId: selectedTreeRow.id },
        {
          name: 'New Roll-Up Account',
          costCodeId: undefined,
          accountType: 'other',
          initialQuantityEstimate: 0,
          currentQuantityEstimate: 0,
          currentQuantityActual: 0,
          initialHoursEstimate: 0,
          currentHoursEstimate: 0,
          currentHoursActual: 0,
        },
      );

      const newTreeRow = await createAccount(accountRequest);
      setSelectedTreeRow(newTreeRow);
    } catch (err) {
      logger.error('Failed to create roll-up account', { err });
      setError(getErrorMessage(err, 'Unknown error occurred'));
    }
  }, [selectedTreeRow, selectedJob, createAccountRequest, createAccount]);

  const createRootNode = useCallback(async () => {
    if (!selectedJob) return;
    try {
      const accountRequest = createAccountRequest(
        { parentId: null },
        {
          name: 'New Root Roll-Up Account',
          costCodeId: undefined,
          accountType: undefined,
          initialQuantityEstimate: 0,
          currentQuantityEstimate: 0,
          currentQuantityActual: 0,
          initialHoursEstimate: 0,
          currentHoursEstimate: 0,
          currentHoursActual: 0,
        },
      );

      const newTreeRow = await createAccount(accountRequest);
      setSelectedTreeRow(newTreeRow);
    } catch (err) {
      logger.error('Failed to create root roll-up account', { err });
      setError(getErrorMessage(err, 'Unknown error occurred'));
    }
  }, [selectedJob, createAccountRequest, createAccount]);

  const addCostCode = useCallback(async () => {
    if (!selectedTreeRow || !selectedJob) return;
    try {
      const parentId = selectedTreeRow.isArtificial ? null : selectedTreeRow.id;
      const accountRequest = createAccountRequest(
        { parentId: parentId },
        {
          costCodeId: undefined,
          accountType: 'other',
          initialQuantityEstimate: 0,
          currentQuantityEstimate: 0,
          currentQuantityActual: 0,
          initialHoursEstimate: 0,
          currentHoursEstimate: 0,
          currentHoursActual: 0,
        },
      );

      const newTreeRow = await createAccount(accountRequest);
      setSelectedTreeRow(newTreeRow);
    } catch (err) {
      logger.error('Failed to add charge code', { err });
      setError(getErrorMessage(err, 'Unknown error occurred'));
    }
  }, [selectedTreeRow, selectedJob, createAccountRequest, createAccount]);

  const duplicateCode = useCallback(async () => {
    if (!selectedTreeRow || !selectedJob || selectedTreeRow.isArtificial)
      return;
    try {
      // The copy must not inherit the CTC link: CreateAccount derives
      // IsCtcConnected from ExternalWbsId, and a second account claiming the
      // same external WBS ID would be undeletable and ambiguous to CTC sync.
      const accountRequest = createAccountRequest(selectedTreeRow, {
        name: `${selectedTreeRow.name} (Copy)`,
        externalWbsId: undefined,
      });

      const newTreeRow = await createAccount(accountRequest);
      setSelectedTreeRow(newTreeRow);
    } catch (err) {
      logger.error('Failed to duplicate charge code', { err });
      setError(getErrorMessage(err, 'Unknown error occurred'));
    }
  }, [selectedTreeRow, selectedJob, createAccountRequest, createAccount]);

  const removeCode = useCallback(async () => {
    if (!selectedTreeRow || !selectedJob || selectedTreeRow.isArtificial)
      return;

    // Check if account has children
    if (selectedTreeRow.children && selectedTreeRow.children.length > 0) {
      setError('Cannot delete account with child accounts');
      return;
    }

    // Check if account has adjustments
    try {
      const adjustmentsCheck = await fetchWbsAdjustments(
        qc,
        selectedTreeRow.id,
      );
      if (adjustmentsCheck && adjustmentsCheck.length > 0) {
        setError('Cannot delete account with adjustments');
        return;
      }
    } catch (err) {
      logger.error('Failed to check for adjustments', { err });
      setError('Failed to check for adjustments');
      return;
    }

    try {
      await deleteAccount(selectedTreeRow.id);
    } catch (err) {
      logger.error('Failed to remove code', { err });
      setError(getErrorMessage(err, 'Unknown error occurred'));
    }
  }, [selectedTreeRow, selectedJob, deleteAccount, wbsData]);

  const onAccountSaved = useCallback(
    async (row: WbsTreeData, updates: Partial<WbsTreeData>) => {
      if (!selectedJob || row.isArtificial) return;
      try {
        const accountRequest = createAccountRequest(row, updates);
        await updateAccount(row.id, accountRequest);
      } catch (err) {
        logger.error('Failed to save account', { err });
        setError(getErrorMessage(err, 'Unknown error occurred'));
        throw err;
      }
    },
    [selectedJob, createAccountRequest, updateAccount],
  );

  // Adjustment Operations
  const loadAdjustments = useCallback(async () => {
    await adjustmentsQuery.refetch();
  }, [adjustmentAccountId]);

  const createAdjustment = useCallback(
    async (adjustmentRequest: WbsAdjustmentRequestDto) => {
      if (!adjustmentAccountId) return;
      await adjustmentMutations.createAdjustment.mutateAsync(adjustmentRequest);
    },
    [adjustmentAccountId, adjustmentMutations.createAdjustment],
  );

  const updateAdjustment = useCallback(
    async (
      adjustmentId: string,
      adjustmentRequest: WbsAdjustmentRequestDto,
    ) => {
      if (!adjustmentAccountId) return;
      await adjustmentMutations.updateAdjustment.mutateAsync({
        adjustmentId,
        request: adjustmentRequest,
      });
    },
    [adjustmentAccountId, adjustmentMutations.updateAdjustment],
  );

  const deleteAdjustment = useCallback(
    async (adjustmentId: string) => {
      if (!adjustmentAccountId) return;
      await adjustmentMutations.deleteAdjustment.mutateAsync(adjustmentId);

      // Clear selection if the deleted adjustment was selected
      if (selectedAdjustment?.id === adjustmentId) {
        setSelectedAdjustment(null);
      }
    },
    [
      adjustmentAccountId,
      adjustmentMutations.deleteAdjustment,
      selectedAdjustment,
    ],
  );

  const selectAdjustment = useCallback(
    (adjustment: WbsAdjustmentDto | null) => {
      setSelectedAdjustment(adjustment);
    },
    [],
  );

  // An adjustment write changes the account's actuals, so the whole tree is
  // refreshed by the mutation's invalidation; this only drops the edit state.
  const onAdjustmentSaved = useCallback(async () => {
    setSelectedAdjustment(null);
  }, []);

  // Adjustment Types
  const loadAdjustmentTypes = useCallback(async () => {
    await adjustmentTypesQuery.refetch();
  }, []);

  React.useEffect(() => {
    if (!selectedTreeRow || selectedTreeRow.isArtificial) {
      setSelectedAdjustment(null);
    }
  }, [selectedTreeRow]);

  // Utility Functions
  const isRollUpAccount = useCallback((row: WbsTreeData) => {
    return !row.isLeaf;
  }, []);

  const isChargeCodeAccount = useCallback((row: WbsTreeData) => {
    return row.isLeaf;
  }, []);

  const isPlaceholderAccount = useCallback((apply: boolean) => {
    return apply === false;
  }, []);

  const isCTCConnectedAccount = useCallback((row: WbsTreeData) => {
    return row.isCtcConnected;
  }, []);

  const getTreeNodeIcon = useCallback(
    (node?: WbsTreeData) => {
      if (!node || node.isArtificial) {
        return React.createElement('span', { className: 'icon-binary-tree' });
      }

      if (node.archived) {
        return React.createElement('span', { className: 'icon-archive' });
      }

      if (isRollUpAccount(node)) {
        return React.createElement('span', { className: 'icon-folder' });
      }

      return React.createElement('span', { className: 'icon-settings-dollar' });
    },
    [isRollUpAccount],
  );

  // Computed Values
  const filteredTreeData: WbsTreeData[] = useMemo(() => {
    const filterArchived = (nodes: WbsTreeData[]): WbsTreeData[] => {
      return nodes
        .filter((node) => !node.archived)
        .map((node) => ({
          ...node,
          children: node.children ? filterArchived(node.children) : [],
        }));
    };

    return filterArchived(treeData);
  }, [treeData]);

  const jobRootNode: WbsTreeData | null = useMemo(() => {
    if (!selectedJob) return null;

    return {
      id: `job-root-${selectedJob.id}`,
      wbsId: '',
      parentId: null,
      costCodeId: null,
      code: '',
      name: selectedJob.jobNumber || 'Job',
      description: '',
      accountType: null,
      initialQuantityEstimate: 0,
      currentQuantityEstimate: 0,
      currentQuantityActual: 0,
      initialHoursEstimate: 0,
      currentHoursEstimate: 0,
      currentHoursActual: 0,
      unitOfMeasure: '$',
      apply: true,
      active: true,
      archived: false,
      timesheetsOnly: false,
      perDiemEligible: false,
      scalePayEnabled: false,
      externalWbsId: null,
      isCtcConnected: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      isLeaf: false,
      children: filteredTreeData,
      isArtificial: true,
      startDate: null,
      finishDate: null,
    };
  }, [selectedJob, filteredTreeData]);

  const isJobRootSelected = useMemo(() => {
    return selectedTreeRow && selectedTreeRow.isArtificial === true;
  }, [selectedTreeRow]);

  const isRollUpAccountSelected = useMemo(() => {
    return (
      selectedTreeRow && isRollUpAccount(selectedTreeRow) && !isJobRootSelected
    );
  }, [selectedTreeRow, isRollUpAccount, isJobRootSelected]);

  const isChargeCodeSelected = useMemo(() => {
    return (
      selectedTreeRow &&
      isChargeCodeAccount(selectedTreeRow) &&
      !isJobRootSelected
    );
  }, [selectedTreeRow, isChargeCodeAccount, isJobRootSelected]);

  const isPlaceholderAccountSelected = useMemo(() => {
    return selectedTreeRow && isPlaceholderAccount(selectedTreeRow.apply);
  }, [selectedTreeRow, isPlaceholderAccount]);

  const isCTCConnectedAccountSelected = useMemo(() => {
    return selectedTreeRow && isCTCConnectedAccount(selectedTreeRow);
  }, [selectedTreeRow, isCTCConnectedAccount]);

  const canCreateRollUpAccount = useMemo(() => {
    return (
      (isRollUpAccountSelected || isJobRootSelected) &&
      !isPlaceholderAccountSelected &&
      !saving
    );
  }, [
    isRollUpAccountSelected,
    isJobRootSelected,
    saving,
    isPlaceholderAccountSelected,
  ]);

  const isProgressManagedSelected = selectedTreeRow?.progressManaged === true;

  const canAddChargeCode = useMemo(() => {
    return (
      (isRollUpAccountSelected || isJobRootSelected || isChargeCodeSelected) &&
      !saving &&
      !isProgressManagedSelected
    );
  }, [
    isRollUpAccountSelected,
    isJobRootSelected,
    isChargeCodeSelected,
    saving,
    isProgressManagedSelected,
  ]);

  const canDuplicate = useMemo(() => {
    return isChargeCodeSelected && !isPlaceholderAccountSelected && !saving;
  }, [isChargeCodeSelected, saving, isPlaceholderAccountSelected]);

  const canDelete = useMemo(() => {
    if (
      !selectedTreeRow ||
      selectedTreeRow.isArtificial ||
      saving ||
      isCTCConnectedAccountSelected ||
      isProgressManagedSelected
    ) {
      return false;
    }

    // Cannot delete if account has children
    if (selectedTreeRow.children && selectedTreeRow.children.length > 0) {
      return false;
    }

    return (
      isRollUpAccountSelected ||
      isChargeCodeSelected ||
      isPlaceholderAccountSelected
    );
  }, [
    selectedTreeRow,
    isRollUpAccountSelected,
    isChargeCodeSelected,
    saving,
    isPlaceholderAccountSelected,
    isCTCConnectedAccountSelected,
  ]);

  // Display Options Operations
  const updateDisplayOptions = useCallback(
    (options: Partial<DisplayOptions>) => {
      setDisplayOptions((prev) => ({
        ...prev,
        ...options,
      }));
    },
    [],
  );

  const contextValue: WbsContextType = {
    // State
    wbsData,
    treeData,
    accountMap,
    selectedTreeRow,
    summary,
    loading,
    saving,
    adjustmentsLoading: adjustmentsQuery.isFetching,
    adjustmentTypesLoading: adjustmentTypesQuery.isFetching,
    error,
    adjustmentsError: adjustmentsQuery.error
      ? getErrorMessage(adjustmentsQuery.error, 'Failed to load adjustments')
      : null,
    adjustmentTypesError: adjustmentTypesQuery.error
      ? getErrorMessage(
          adjustmentTypesQuery.error,
          'Failed to load adjustment types',
        )
      : null,
    adjustments,
    adjustmentTypes,
    selectedAdjustment,
    adjustmentsRefreshKey: adjustmentsQuery.dataUpdatedAt,
    jobRootNode,
    isJobRootSelected,
    isRollUpAccountSelected,
    isChargeCodeSelected,
    isPlaceholderAccountSelected,
    isCTCConnectedAccountSelected,
    canCreateRollUpAccount,
    canAddChargeCode,
    canDuplicate,
    canDelete,
    displayOptions,
    wbsDirty,

    // Actions
    loadWBS,
    saveWBS,
    refreshWbs,
    markWbsStale,
    selectTreeRow,
    onTreeSelect,
    findRootAncestor,
    collectLeafDescendants,
    createAccount,
    updateAccount,
    deleteAccount,
    createRollUpAccount,
    createRootNode,
    addCostCode,
    duplicateCode,
    removeCode,
    onAccountSaved,
    loadAdjustments,
    createAdjustment,
    updateAdjustment,
    deleteAdjustment,
    selectAdjustment,
    onAdjustmentSaved,
    loadAdjustmentTypes,
    createAccountRequest,
    getTreeNodeIcon,
    isRollUpAccount,
    isPlaceholderAccount,
    isChargeCodeAccount,
    isCTCConnectedAccount,
    updateDisplayOptions,
  };

  return (
    <WbsContext.Provider value={contextValue}>{children}</WbsContext.Provider>
  );
};
